<template>
  <p class="has-text-weight-bold is-size-4">Detalhes empresa</p>

  <div v-if="error" class="container">
    <div class="box has-text-centered">
      <span class="fa fa-exclamation-triangle has-text-danger is-size-2 mb-4" aria-hidden="true"></span>
      <p class="has-text-weight-semibold is-size-5">Erro ao carregar dados da empresa</p>
      <p class="has-text-grey is-size-7 mt-2">Verifique a sua ligação à internet e tente novamente.</p>
      <button class="button is-primary mt-4" @click="retry">Tentar novamente</button>
    </div>
  </div>

  <template v-else-if="!isDataLoaded && !error">
    <div class="container">
      <div class="box">
        <div class="columns">
          <div class="column is-4 is-flex is-flex-direction-column is-justify-content-center is-align-items-center">
            <div class="skeleton-circle"></div>
            <div class="skeleton-line skeleton-line-short mt-6"></div>
            <div class="skeleton-line skeleton-line-xs mt-2"></div>
            <div class="skeleton-line skeleton-line-xs mt-5"></div>
            <div class="skeleton-line skeleton-line-medium mt-3"></div>
          </div>
          <div class="column is-8">
            <div class="skeleton-line skeleton-line-medium"></div>
            <div class="skeleton-line skeleton-line-long mt-3"></div>
            <div class="skeleton-line skeleton-line-long mt-2"></div>
            <div class="skeleton-line skeleton-line-medium mt-2"></div>
            <div class="skeleton-line skeleton-line-long mt-5"></div>
            <div class="skeleton-line skeleton-line-medium mt-2"></div>
            <div class="skeleton-line skeleton-line-long mt-2"></div>
          </div>
        </div>
      </div>
      <div class="box mt-4">
        <div class="skeleton-line skeleton-line-medium"></div>
        <div class="columns mt-4">
          <div class="column">
            <div class="skeleton-line skeleton-line-long"></div>
            <div class="skeleton-line skeleton-line-long mt-2"></div>
          </div>
          <div class="column">
            <div class="skeleton-line skeleton-line-long"></div>
            <div class="skeleton-line skeleton-line-long mt-2"></div>
          </div>
          <div class="column">
            <div class="skeleton-line skeleton-line-long"></div>
            <div class="skeleton-line skeleton-line-long mt-2"></div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <template v-else>
    <div class="container">
      <div class="box">
        <div class="columns">
          <div
              class="column right-border is-4 is-flex is-flex-direction-column is-justify-content-center is-align-items-center">
            <div v-if="hasLogo" :style="logoStyle" class="appLogo"></div>
            <div v-else class="fallback-avatar">
              <span class="fallback-initials">{{ companyInitials }}</span>
            </div>
            <div class="mt-6 has-text-centered">
              <p class="has-text-grey-dark has-text-weight-semibold">{{ company.nome_goldylocks }}</p>
              <p class="has-text-grey-light is-size-7">Criado em {{ company.data_instalacao }}</p>
            </div>
            <div class="mt-3 top-border">
              <div class="mt-3">
                <span class="is-size-7 has-text-grey">Plano ativo </span><br>
                <span class="tag is-medium is-uppercase has-text-weight-semibold" :class="planBadgeClass">{{ company.plan }}</span>
              </div>
              <div class="mt-3">
                <a :href="appURL" class="button is-primary is-small has-text-weight-semibold"
                   target="_blank">Aceder</a>
              </div>
            </div>
          </div>
          <div class="column is-8">
            <div class="is-flex is-justify-content-end">
              <CompanyDetailsDropdown :company="company"></CompanyDetailsDropdown>
            </div>
            <p class="has-text-grey-light has-text-weight-semibold mt-4">Dados empresa</p>
            <div class="columns mt-1">
              <div class="column">
                <p class="has-text-grey-dark has-text-weight-semibold">NIF</p>
                <p class="mt-1 has-text-grey-light has-text-weight-semibold">{{ company.nif_goldylocks }}</p>
              </div>
              <div class="column">
                <p class="has-text-grey-dark has-text-weight-semibold">Contabilista&nbsp;<a v-if="!editingAccountant"
                                                                                            class="is-size-7"
                                                                                            @click="editAccountant">alterar</a>
                </p>
                <p v-if="!editingAccountant" class="mt-1 has-text-grey-light has-text-weight-semibold">
                  {{ accountantEmail }}</p>
                <HiddenInput v-else :value="accountantEmail" type="email" @save="saveAccountant"></HiddenInput>
              </div>
              <div v-if="isAdministration" class="column">
                <div v-if="profile.type === '1'">
                  <p class="has-text-grey-dark has-text-weight-semibold">Agente&nbsp;<a v-if="!editingReseller"
                                                                                        class="is-size-7"
                                                                                        @click="editReseller">alterar</a>
                  </p>
                  <p v-if="!editingReseller" class="mt-1 has-text-grey-light has-text-weight-semibold">{{
                      resellerEmail
                    }}</p>
                  <HiddenInput v-else :value="resellerEmail" type="email" @save="saveReseller"></HiddenInput>
                </div>
              </div>
            </div>
            <div class="top-border mt-4">
              <p class="has-text-grey-light has-text-weight-semibold mt-4">Informação</p>
              <div class="columns mt-1">
                <div class="column">
                  <p class="has-text-grey-dark has-text-weight-semibold">Morada</p>
                  <p class="mt-1 has-text-grey-light has-text-weight-semibold">{{ company.morada_goldylocks }}</p>
                </div>
                <div class="column">
                  <p class="has-text-grey-dark has-text-weight-semibold">Código Postal</p>
                  <p class="mt-1 has-text-grey-light has-text-weight-semibold">{{ company.cp_goldylocks }}</p>
                </div>
                <div class="column">
                  <p class="has-text-grey-dark has-text-weight-semibold">Localidade</p>
                  <p class="mt-1 has-text-grey-light has-text-weight-semibold">{{ company.localidade_goldylocks }}</p>
                </div>
              </div>
            </div>
            <div v-if="isAdministration" class="top-border mt-4">
              <p class="has-text-grey-light has-text-weight-semibold mt-4"><span class="fa fa-wrench" aria-hidden="true"></span> Informação
                técnica</p>
              <div class="columns mt-1">
                <div class="column">
                  <p class="has-text-grey-dark has-text-weight-semibold"><span class="fa fa-code-branch" aria-hidden="true"></span> Git
                    Branch</p>
                  <div class="select is-dark is-small">
                    <select v-model="company.git_branch" @change="changeCompanyGitBranch">
                      <option value="alpha">Alpha</option>
                      <option value="beta">Beta</option>
                      <option value="master">Master</option>
                    </select>
                  </div>
                </div>
                <div class="column">
                  <p class="has-text-grey-dark has-text-weight-semibold">Acesso técnico</p>
                  <a :href="techURL" class="button is-dark is-small has-text-weight-semibold mt-1" target="_blank"><span
                      class="fa fa-external-link-alt" aria-hidden="true"></span>&nbsp;Abrir</a>
                </div>
              </div>
            </div>
            <div v-if="isAdministration" class="top-border mt-4">
              <p class="has-text-grey-light has-text-weight-semibold mt-4"><span class="fa fa-calendar-alt" aria-hidden="true"></span> Controlo licenciamento</p>
              <ModulesControlTable :company="company"></ModulesControlTable>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <PlansTable :company="company"></PlansTable>
    </div>
  </template>
</template>

<script>
import Swal from "sweetalert2";
import PlansTable from "@/components/tables/PlansTable";
import HiddenInput from "@/components/inputs/hiddenInput";
import CompanyDetailsDropdown from "@/components/menus/CompanyDetailsDropdown";
import ModulesControlTable from "@/components/tables/ModulesControlTable";

export default {
  name: "CompanyDetails",
  components: {
    ModulesControlTable,
    CompanyDetailsDropdown,
    HiddenInput,
    PlansTable
  },
  data() {
    return {
      isDataLoaded: false,
      error: false,
      editingAccountant: false,
      editingReseller: false
    }
  },
  methods: {
    editAccountant() {
      this.editingAccountant = !this.editingAccountant;
    },
    editReseller() {
      this.editingReseller = !this.editingReseller;
    },
    saveAccountant(newAccountantEmail) {
      this.$store.dispatch('updateCompanyAccountant', newAccountantEmail)
          .then(() => {
            this.$store.dispatch('fetchCompanyDetails', this.$store.getters.companyDetails['id_goldylocks']);
            this.editAccountant();
            Swal.fire({
              icon: 'success',
              text: 'Email do contabilista atualizado com sucesso!',
              toast: true,
              timer: 3000,
              position: 'top-end',
              showConfirmButton: false
            });
          }).catch(() => {
            Swal.fire({
              icon: 'error',
              text: 'Erro ao atualizar email do contabilista.',
              toast: true,
              timer: 3000,
              position: 'top-end',
              showConfirmButton: false
            });
          })
    },
    saveReseller(newResellerEmail) {
      this.$store.dispatch('updateCompanyReseller', newResellerEmail)
          .then(() => {
            this.$store.dispatch('fetchCompanyDetails', this.$store.getters.companyDetails['id_goldylocks']);
            this.editReseller();
            Swal.fire({
              icon: 'success',
              text: 'Email do agente atualizado com sucesso!',
              toast: true,
              timer: 3000,
              position: 'top-end',
              showConfirmButton: false
            });
          }).catch(() => {
            Swal.fire({
              icon: 'error',
              text: 'Erro ao atualizar email do agente.',
              toast: true,
              timer: 3000,
              position: 'top-end',
              showConfirmButton: false
            });
          })
    },
    changeCompanyGitBranch() {
      this.$store.dispatch('changeCompanyGitBranch', {
        companyId: this.$store.getters.companyDetails['id_goldylocks'],
        newGitBranch: this.company.git_branch
      }).then(() => {
        Swal.fire({
          icon: 'success',
          text: 'Branch alterado com sucesso!',
          toast: true,
          timer: 3000,
          position: 'top-end',
          showConfirmButton: false
        });
      }).catch(() => {
        Swal.fire({
          icon: 'error',
          text: 'Erro ao alterar o branch.',
          toast: true,
          timer: 3000,
          position: 'top-end',
          showConfirmButton: false
        });
      })
    },
    retry() {
      this.error = false;
      this.isDataLoaded = false;
      this.fetchData();
    },
    fetchData() {
      this.$store.dispatch('fetchCompanyDetails', this.$route.params.id)
        .then(() => {
          this.isDataLoaded = true;
        })
        .catch(() => {
          this.error = true;
          this.isDataLoaded = true;
        })
      this.$store.dispatch('fetchResellers')
    }
  },
  computed: {
    isAdministration() {
      return (this.$store.getters.profile.type === "1");
    },
    profile() {
      return this.$store.getters.profile;
    },
    hasLogo() {
      return !!this.company.alias_goldylocks;
    },
    companyInitials() {
      const name = this.company.nome_goldylocks || '';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name ? name.substring(0, 2).toUpperCase() : '--';
    },
    logoStyle() {
      if (this.company.alias_goldylocks) {
        return `background:url(https://app.goldylocks.pt/${this.company.alias_goldylocks}/api/logotipo);`
      }
    },
    company() {
      return this.$store.getters.companyDetails;
    },
    planBadgeClass() {
      if (this.company.plan === "green") {
        return 'is-success';
      } else if (this.company.plan === "blue") {
        return 'is-info';
      } else if (this.company.plan === "orange") {
        return 'is-warning';
      } else {
        return 'is-light';
      }
    },
    phone() {
      return this.company.telefone_cliente ? this.company.telefone_cliente : '--';
    },
    mobile() {
      return this.company.telemovel_cliente ? this.company.telemovel_cliente : '--';
    },
    email() {
      return this.company.email_empresa ? this.company.email_empresa : '--';
    },
    accountantEmail() {
      if (this.company.contabilista_email) {
        return this.company.contabilista_email;
      } else {
        return '--';
      }
    },
    resellerEmail() {
      if (this.company.agente_email) {
        return this.company.agente_email;
      } else {
        return '--';
      }
    },
    appURL() {
      return `https://app.goldylocks.pt/${this.company['alias_goldylocks']}`
    },
    techURL() {
      return `https://app.goldylocks.pt/${this.company['alias_goldylocks']}/?hash=${this.company['url_hash']}`;
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>

<style scoped>
.top-border {
  border-top: 1px solid #eaeaea;
}

@media screen and (min-width: 768px) {
  .right-border {
    border-right: 1px solid #eaeaea;
  }
}

.appLogo {
  width: 128px;
  height: 128px;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  border-radius: 64px;
  border: 5px solid #EBEBEB;
}

.fallback-avatar {
  width: 128px;
  height: 128px;
  border-radius: 64px;
  border: 5px solid #EBEBEB;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fallback-initials {
  font-size: 2.5rem;
  font-weight: 700;
  color: #b0b0b0;
  text-transform: uppercase;
}

.skeleton-circle {
  width: 128px;
  height: 128px;
  border-radius: 64px;
  background: #e8e8e8;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-line {
  height: 14px;
  border-radius: 7px;
  background: #e8e8e8;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-line-xs {
  width: 30%;
}

.skeleton-line-short {
  width: 50%;
}

.skeleton-line-medium {
  width: 70%;
}

.skeleton-line-long {
  width: 100%;
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}
</style>
